import React, { useEffect, useState } from 'react';
import {
  Filter,
  ChevronDown,
  Heart,
  ShoppingBag,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Clock,
  Tag,
  LogIn,
  UserPlus,
} from 'lucide-react';
import { Product, WishlistItem, User as UserType } from '../types';

interface ShopProps {
  initialCategory: 'all' | 'jewelry' | 'bags' | 'fragrances';
  onNavigate: (page: string) => void;
  products: Product[];
  wishlist: WishlistItem[];
  onToggleWishlist: (product: Product) => void | Promise<void>;
  onAddToCart: (product: Product, quantity: number) => Promise<boolean> | void;
  onSelectProduct?: (product: Product) => void;
  user: UserType | null;
}

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://sparklinggems.shop/api'
    : 'http://localhost/sparkling-gems-backend-php/api';

const PLACEHOLDER_IMAGE =
  'https://placehold.co/400x500/f5f5f5/999999?text=No+Image';

const Shop: React.FC<ShopProps> = ({
  initialCategory,
  onNavigate,
  products: initialProducts,
  wishlist,
  onToggleWishlist,
  onAddToCart,
  onSelectProduct,
  user,
}) => {
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [visibleProducts, setVisibleProducts] = useState<Product[]>([]);

  const [apiProducts, setApiProducts] = useState<Product[]>([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [wishlistLoading, setWishlistLoading] = useState<Record<number, boolean>>({});
  const [cartLoading, setCartLoading] = useState<Record<number, boolean>>({});
  const [pendingWishlistAction, setPendingWishlistAction] = useState<Record<number, boolean>>({});

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [promptAction, setPromptAction] = useState<'cart' | 'wishlist'>('cart');

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchProductsFromAPI();
  }, []);

  useEffect(() => {
    setActiveCategory(initialCategory);
  }, [initialCategory]);

  useEffect(() => {
    const selectedProductJson = localStorage.getItem('selectedProduct');
    const selectedProductId = localStorage.getItem('selectedProductId');

    if (selectedProductJson && onSelectProduct) {
      try {
        const product = JSON.parse(selectedProductJson);
        onSelectProduct(product);
        localStorage.removeItem('selectedProduct');
        localStorage.removeItem('selectedProductId');
      } catch (e) {
        console.error('Error parsing selected product:', e);
      }
    } else if (selectedProductId && onSelectProduct && apiProducts.length > 0) {
      const product = apiProducts.find((p) => p.id === parseInt(selectedProductId, 10));
      if (product) {
        onSelectProduct(product);
        localStorage.removeItem('selectedProductId');
      }
    }
  }, [apiProducts, onSelectProduct]);

  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(() => {
      const sourceProducts = apiProducts.length > 0 ? apiProducts : initialProducts;

      let filtered = sourceProducts.filter((p) => {
        if (activeCategory === 'all') return true;
        return p.category === activeCategory;
      });

      if (sortBy === 'price-asc') {
        filtered = [...filtered].sort((a, b) => a.price - b.price);
      } else if (sortBy === 'price-desc') {
        filtered = [...filtered].sort((a, b) => b.price - a.price);
      } else if (sortBy === 'newest') {
        filtered = [...filtered].sort(
          (a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)
        );
      }

      setVisibleProducts(filtered);
      setLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [activeCategory, sortBy, apiProducts, initialProducts]);

  const heroImages = {
    all: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2070&auto=format&fit=crop',
    jewelry:
      'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?q=80&w=2000&auto=format&fit=crop',
    bags: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?q=80&w=2069&auto=format&fit=crop',
    fragrances:
      'https://images.unsplash.com/photo-1616949755610-8c9bbc08f138?q=80&w=2070&auto=format&fit=crop',
  };

  const heroTitles: Record<string, string> = {
    all: 'The Grand Collection',
    jewelry: 'High Jewelry',
    bags: 'Leather Goods',
    fragrances: 'Haute Parfumerie',
  };

  const heroSubtitles: Record<string, string> = {
    all: 'Explore our complete range of hand-selected luxuries.',
    jewelry: 'Diamonds, gemstones, and precious metals crafted for eternity.',
    bags: 'Exquisite Italian craftsmanship meeting modern utility.',
    fragrances: 'Scented masterpieces to define your presence.',
  };

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    const toast = document.createElement('div');
    toast.className = [
      'fixed top-24 right-4 text-white px-6 py-3 rounded-sm shadow-lg z-[300]',
      type === 'success' ? 'bg-green-600' : 'bg-red-600',
    ].join(' ');
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  function getUserId(): number | null {
    if (user?.id) return user.id;

    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const parsedUser = JSON.parse(userStr);
        if (parsedUser?.id) return Number(parsedUser.id);
      }

      const id = localStorage.getItem('userId');
      return id ? parseInt(id, 10) : null;
    } catch {
      return null;
    }
  }

  function getGuestId(): string {
    let guestId = localStorage.getItem('guest_id');
    if (!guestId) {
      guestId =
        'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('guest_id', guestId);
    }
    return guestId;
  }

  function isAuthenticated() {
    return !!getUserId();
  }

  async function fetchProductsFromAPI() {
    setApiLoading(true);
    setApiError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/products/index.php`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const productsArray = data.products || data.data || data;

      if (!Array.isArray(productsArray)) {
        setApiProducts(initialProducts);
        return;
      }

      const transformedProducts: Product[] = productsArray.map((item: any) => {
        let galleryImages: string[] = [];

        if (item.gallery_images) {
          try {
            if (typeof item.gallery_images === 'string') {
              try {
                const parsed = JSON.parse(item.gallery_images);
                galleryImages = Array.isArray(parsed) ? parsed : [];
              } catch {
                if (item.gallery_images.includes(',')) {
                  galleryImages = item.gallery_images
                    .split(',')
                    .map((url: string) => url.trim());
                } else if (item.gallery_images.length > 10) {
                  galleryImages = [item.gallery_images];
                }
              }
            } else if (Array.isArray(item.gallery_images)) {
              galleryImages = item.gallery_images;
            }

            galleryImages = galleryImages.filter(
              (img: string) => img && typeof img === 'string' && img.length > 10
            );
          } catch {
            galleryImages = [];
          }
        }

        let weight: string | number | undefined = undefined;
        if (item.weight) {
          const parsedWeight = parseFloat(item.weight);
          weight = !isNaN(parsedWeight) ? parsedWeight : item.weight;
        }

        // FIXED: Ensure image is properly mapped from database
        let productImage = PLACEHOLDER_IMAGE;
        if (item.image_url && item.image_url.trim() !== '') {
          productImage = item.image_url;
        } else if (item.image && item.image.trim() !== '') {
          productImage = item.image;
        }

        console.log(`📦 Product ${item.id} - ${item.name}: image = ${productImage}`);

        return {
          id: Number(item.id),
          name: item.name,
          description: item.description || '',
          price: parseFloat(item.price) || 0,
          image: productImage,
          galleryImages,
          category: item.category || 'jewelry',
          isNew: item.is_new == 1 || false,
          isBestSeller: item.is_best_seller == 1 || false,
          condition: item.condition || 'Brand New',
          weight,
          karat: item.karat || '',
          goldType: item.gold_type || item.goldType || '',
          stock:
            item.stock === null || item.stock === undefined
              ? 0
              : parseInt(item.stock, 10) || 0,
          isPreOrder: item.isPreOrder == 1 || false,
          preOrderReleaseDate: item.preOrderReleaseDate || null,
          priceOverridden: item.priceOverridden == 1 || false,
          is_promo: item.is_promo == 1 || false,
          promo_price: item.promo_price ? parseFloat(item.promo_price) : null,
          promo_percentage: item.promo_percentage
            ? parseInt(item.promo_percentage, 10)
            : null,
          promo_label: item.promo_label || null,
          promo_start_date: item.promo_start_date || null,
          promo_end_date: item.promo_end_date || null,
        } as Product;
      });

      setApiProducts(transformedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      setApiError('Failed to load products from server');
      setApiProducts(initialProducts);
    } finally {
      setApiLoading(false);
    }
  }

  const handleImageError = (productId: number) => {
    if (!imageErrors[productId]) {
      setImageErrors((prev) => ({ ...prev, [productId]: true }));
      console.log(`🖼️ Image failed to load for product ${productId}`);
    }
  };

  const getProductImage = (product: Product): string => {
    if (imageErrors[product.id]) {
      console.log(`⚠️ Using placeholder for product ${product.id} due to image error`);
      return PLACEHOLDER_IMAGE;
    }
    if (product.image && product.image.trim() !== '' && product.image.length > 5) {
      return product.image;
    }
    return PLACEHOLDER_IMAGE;
  };

  const openLightbox = (
    images: string[],
    index: number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (images && images.length > 0) {
      setLightboxImages(images);
      setCurrentImageIndex(index);
      setLightboxOpen(true);
    }
  };

  const isInWishlist = (id: number) => {
    return wishlist.some((item) => item.id === id);
  };

  const handleProductClick = (product: Product) => {
    if (onSelectProduct) {
      onSelectProduct(product);
    }
  };

  // FIXED: Log the product being added to cart
  const handleAddToCart = async (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();

    console.log('🛒 ADDING TO CART:', {
      id: product.id,
      name: product.name,
      image: product.image,
      price: product.price,
      stock: product.stock
    });

    if (!user && !isAuthenticated()) {
      const guestId = getGuestId();
      if (!guestId) {
        setPromptAction('cart');
        setShowLoginPrompt(true);
        return;
      }
    }

    if (typeof product.stock === 'number' && product.stock <= 0) {
      showToast('This item is out of stock.', 'error');
      return;
    }

    setCartLoading((prev) => ({ ...prev, [product.id]: true }));

    try {
      // The onAddToCart will call the API with the product
      // The backend will fetch the product image from the database
      const result = await Promise.resolve(onAddToCart(product, 1));

      if (result === false) {
        showToast('Failed to add item to cart. Please try again.', 'error');
      } else {
        showToast('Added to cart successfully');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast('Failed to add item to cart. Please try again.', 'error');
    } finally {
      setCartLoading((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const handleWishlistToggle = async (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    e.preventDefault();

    if (pendingWishlistAction[product.id] || wishlistLoading[product.id]) {
      console.log('Wishlist action already in progress, ignoring click');
      return;
    }

    if (!user && !isAuthenticated()) {
      const guestId = getGuestId();
      if (!guestId) {
        setPromptAction('wishlist');
        setShowLoginPrompt(true);
        return;
      }
    }

    setPendingWishlistAction((prev) => ({ ...prev, [product.id]: true }));
    setWishlistLoading((prev) => ({ ...prev, [product.id]: true }));

    try {
      const currentlyInWishlist = isInWishlist(product.id);

      console.log('🔄 Delegating wishlist toggle to App.tsx for product:', product.id, {
        currentlyInWishlist,
        productName: product.name,
      });

      await Promise.resolve(onToggleWishlist(product));

      showToast(
        currentlyInWishlist ? 'Removed from wishlist' : 'Added to wishlist',
        'success'
      );
    } catch (error) {
      console.error('❌ Error toggling wishlist:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to update wishlist. Please try again.',
        'error'
      );
    } finally {
      setWishlistLoading((prev) => ({ ...prev, [product.id]: false }));
      setPendingWishlistAction((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const refreshProducts = () => {
    setImageErrors({});
    fetchProductsFromAPI();
  };

  const formatPrice = (price: number): string => {
    return `₱${price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getConditionBadgeStyle = (condition: string) => {
    switch (condition) {
      case 'Brand New':
        return 'bg-emerald-500/90 text-white';
      case 'Pre-loved':
      case 'Pre-loved (Used)':
        return 'bg-amber-500/90 text-white';
      case 'Vintage':
        return 'bg-purple-500/90 text-white';
      case 'Like New':
        return 'bg-blue-500/90 text-white';
      case 'Good':
        return 'bg-green-500/90 text-white';
      case 'Fair':
        return 'bg-orange-500/90 text-white';
      default:
        return 'bg-stone-800/90 text-gold-200';
    }
  };

  const getConditionDisplay = (condition: string) => {
    switch (condition) {
      case 'Pre-loved':
      case 'Pre-loved (Used)':
        return '🔄 Pre-loved';
      case 'Vintage':
        return '📅 Vintage';
      case 'Like New':
        return '✨ Like New';
      case 'Good':
        return '👍 Good';
      case 'Fair':
        return '⚠️ Fair';
      default:
        return condition;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      {showLoginPrompt && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-stone-900/80 backdrop-blur-md"
            onClick={() => setShowLoginPrompt(false)}
          />
          <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden z-[301]">
            <div className="bg-stone-900 text-white p-6 text-center">
              <div className="w-16 h-16 bg-gold-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                {promptAction === 'cart' ? (
                  <ShoppingBag size={32} className="text-gold-400" />
                ) : (
                  <Heart size={32} className="text-gold-400" />
                )}
              </div>
              <h3 className="font-serif text-2xl mb-2">Login Required</h3>
              <p className="text-stone-400 text-sm">
                Please login or create an account to add items to your {promptAction}.
              </p>
            </div>

            <div className="p-8 space-y-4">
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  onNavigate('auth');
                }}
                className="w-full bg-gold-600 text-white py-3 rounded-sm hover:bg-gold-700 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-widest"
              >
                <LogIn size={18} />
                Login to Your Account
              </button>

              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  onNavigate('register');
                }}
                className="w-full border border-stone-300 text-stone-700 py-3 rounded-sm hover:bg-stone-50 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-widest"
              >
                <UserPlus size={18} />
                Create New Account
              </button>

              <button
                onClick={() => setShowLoginPrompt(false)}
                className="w-full text-stone-500 py-2 text-xs hover:text-stone-700 transition-colors"
              >
                Continue Browsing
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="relative h-[50vh] overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={heroImages[activeCategory] || heroImages.all}
            alt={activeCategory}
            className="w-full h-full object-cover animate-[zoomSlow_20s_infinite_alternate]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = heroImages.all;
            }}
          />
          <div className="absolute inset-0 bg-stone-900/40" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl text-white mt-10">
          <span className="text-gold-300 text-xs tracking-[0.3em] uppercase font-bold block mb-4">
            Official Jewelry Shop
          </span>
          <h1 className="font-serif text-5xl md:text-7xl mb-6 shadow-sm">
            {heroTitles[activeCategory] || heroTitles.all}
          </h1>
          <p className="text-stone-100 text-lg font-light max-w-xl mx-auto backdrop-blur-sm bg-stone-900/10 p-4 rounded-sm">
            {heroSubtitles[activeCategory] || heroSubtitles.all}
          </p>
        </div>
      </section>

<div className="sticky top-24 z-40 bg-white/90 backdrop-blur-md border-b border-stone-200 py-4 px-6 shadow-sm">
  <div className="max-w-[1440px] mx-auto">
    {/* Category Row - Now separate */}
    <div className="flex items-center justify-center md:justify-start overflow-x-auto pb-3 md:pb-4 scrollbar-hide">
      <div className="flex items-center gap-2">
        {['all', 'jewelry', 'bags', 'fragrances'].map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat as any)}
            className={`px-6 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap transition-all duration-300 ${
              activeCategory === cat
                ? 'bg-stone-900 text-white shadow-lg'
                : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
            }`}
          >
            {cat === 'all' ? 'View All' : cat}
          </button>
        ))}
      </div>
    </div>

    {/* Filter and Sort Row - Separate from categories */}
    <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-2 border-t border-stone-100">
      <div className="flex items-center gap-2 text-stone-500 text-xs tracking-widest">
        <span>{visibleProducts.length} Items</span>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-stone-800 hover:text-gold-600 transition-colors text-xs uppercase font-bold"
        >
          <Filter size={16} /> Filters
        </button>

        <div className="relative group">
          <button className="flex items-center gap-2 text-stone-800 hover:text-gold-600 transition-colors text-xs uppercase font-bold">
            Sort <ChevronDown size={14} />
          </button>
          <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-stone-100 shadow-xl rounded-sm py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50">
            {[
              { label: 'Featured', val: 'featured' },
              { label: 'Newest Arrivals', val: 'newest' },
              { label: 'Price: Low to High', val: 'price-asc' },
              { label: 'Price: High to Low', val: 'price-desc' },
            ].map((opt) => (
              <button
                key={opt.val}
                onClick={() => setSortBy(opt.val)}
                className={`block w-full text-left px-4 py-3 text-xs uppercase tracking-wider hover:bg-stone-50 ${
                  sortBy === opt.val
                    ? 'text-gold-600 font-bold'
                    : 'text-stone-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={refreshProducts}
          className="text-stone-400 hover:text-gold-600 transition-colors"
          title="Refresh products"
        >
          <RefreshCw size={16} className={apiLoading ? 'animate-spin' : ''} />
        </button>
      </div>
    </div>
  </div>
</div>

      {apiError && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-6 my-4">
          <div className="flex items-center">
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {apiError} - Showing available products
              </p>
            </div>
            <button
              onClick={refreshProducts}
              className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <section className="max-w-[1440px] mx-auto px-6 py-12 min-h-[600px]">
        {loading || apiLoading ? (
          <div className="w-full h-96 flex flex-col items-center justify-center text-gold-600">
            <div className="w-12 h-12 border-4 border-gold-200 border-t-gold-600 rounded-full animate-spin mb-4" />
            <span className="text-xs tracking-widest uppercase">
              Loading treasures...
            </span>
          </div>
        ) : visibleProducts.length === 0 ? (
          <div className="text-center py-20">
            <h3 className="font-serif text-2xl text-stone-400">No products found.</h3>
            <button
              onClick={() => {
                setActiveCategory('all');
                setShowFilters(false);
              }}
              className="mt-4 text-xs underline uppercase tracking-widest text-gold-600 hover:text-gold-500"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {visibleProducts.map((product) => {
              const productImage = getProductImage(product);
              const isWishlistBusy = !!wishlistLoading[product.id] || !!pendingWishlistAction[product.id];
              const isCartBusy = !!cartLoading[product.id];

              const extendedProduct = product as any;
              const isPromo =
                extendedProduct.is_promo === true ||
                extendedProduct.is_promo === 1 ||
                extendedProduct.is_promo === '1' ||
                (extendedProduct.promo_price && extendedProduct.promo_price > 0);

              const promoPrice = extendedProduct.promo_price;
              const promoPercentage = extendedProduct.promo_percentage;
              const promoLabel = extendedProduct.promo_label || 'SALE';
              const promoEndDate = extendedProduct.promo_end_date;
              const formattedEndDate = formatDate(promoEndDate);

              const isPromoActive =
                !promoEndDate || new Date(promoEndDate) >= new Date();
              const showPromo = isPromo && isPromoActive && promoPrice;

              const displayPrice = showPromo ? promoPrice : product.price || 0;
              const originalPrice = showPromo ? product.price : null;

              const isPreLoved =
                product.condition === 'Pre-loved' ||
                product.condition === 'Pre-loved (Used)';
              const isPreOrder = extendedProduct.isPreOrder || false;

              const outOfStock =
                typeof product.stock === 'number' && product.stock <= 0;

              let displayWeight = '';
              if (product.weight) {
                if (typeof product.weight === 'number') {
                  displayWeight = product.weight.toFixed(1);
                } else {
                  const parsed = parseFloat(product.weight);
                  displayWeight = !isNaN(parsed)
                    ? parsed.toFixed(1)
                    : String(product.weight);
                }
              }

              return (
                <div
                  key={product.id}
                  className="group relative cursor-pointer"
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  onClick={() => handleProductClick(product)}
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-stone-50 mb-2">
                    <div className="absolute top-3 left-3 z-20 flex flex-col gap-2">
                      {showPromo && (
                        <div className="flex flex-col gap-1">
                          <span className="bg-red-600/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm flex items-center gap-1">
                            <Tag size={12} /> {promoLabel}
                          </span>
                          {promoPercentage && (
                            <span className="bg-red-500/90 backdrop-blur text-white text-[8px] uppercase font-bold px-2 py-0.5 tracking-wider shadow-sm">
                              -{promoPercentage}% OFF
                            </span>
                          )}
                        </div>
                      )}

                      {product.isNew && !showPromo && (
                        <span className="bg-white/90 backdrop-blur text-stone-900 text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm">
                          New Arrival
                        </span>
                      )}

                      {product.isBestSeller && !showPromo && (
                        <span className="bg-gold-600/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm">
                          Best Seller
                        </span>
                      )}

                      {isPreOrder && !showPromo && (
                        <span className="bg-purple-600/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm flex items-center gap-1">
                          <Clock size={12} /> Pre-Order
                        </span>
                      )}

                      {outOfStock && (
                        <span className="bg-red-700/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm">
                          Out of Stock
                        </span>
                      )}
                    </div>

                    {product.condition &&
                      product.condition !== 'Brand New' &&
                      !isPreOrder &&
                      !showPromo && (
                        <div className="absolute top-3 right-3 z-20">
                          <span
                            className={`backdrop-blur text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm flex items-center gap-1 ${getConditionBadgeStyle(
                              product.condition
                            )}`}
                          >
                            {getConditionDisplay(product.condition)}
                          </span>
                        </div>
                      )}

                    {product.condition === 'Brand New' &&
                      !product.isNew &&
                      !product.isBestSeller &&
                      !isPreOrder &&
                      !showPromo && (
                        <div className="absolute top-3 left-3 z-20">
                          <span
                            className={`backdrop-blur text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm ${getConditionBadgeStyle(
                              product.condition
                            )}`}
                          >
                            {product.condition}
                          </span>
                        </div>
                      )}

                    <button
                      onClick={(e) => handleWishlistToggle(e, product)}
                      disabled={isWishlistBusy}
                      className={`absolute top-3 right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isInWishlist(product.id)
                          ? 'bg-red-50 text-red-500'
                          : 'bg-white/50 text-white hover:bg-white hover:text-red-500'
                      } ${isWishlistBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{
                        right:
                          product.condition &&
                          product.condition !== 'Brand New' &&
                          !isPreOrder &&
                          !showPromo
                            ? '3.5rem'
                            : '0.75rem',
                      }}
                    >
                      {isWishlistBusy ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Heart
                          size={18}
                          fill={isInWishlist(product.id) ? 'currentColor' : 'none'}
                        />
                      )}
                    </button>

                    {product.galleryImages && product.galleryImages.length > 0 && (
                      <div className="absolute bottom-3 left-3 z-20 flex gap-1">
                        <div className="flex -space-x-1">
                          {product.galleryImages.slice(0, 3).map((_, idx) => (
                            <div
                              key={idx}
                              className="w-1.5 h-1.5 rounded-full bg-white/80 shadow-sm"
                            />
                          ))}
                          {product.galleryImages.length > 3 && (
                            <span className="text-[8px] text-white ml-1">
                              +{product.galleryImages.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <img
                      src={productImage}
                      alt={product.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={() => handleImageError(product.id)}
                    />

                    <div
                      className={`absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-stone-900 via-stone-900/80 to-transparent transition-transform duration-300 transform ${
                        hoveredProduct === product.id
                          ? 'translate-y-0'
                          : 'translate-y-full'
                      }`}
                    >

                        {product.galleryImages && product.galleryImages.length > 0 && (
                          <button
                            className="w-10 bg-stone-800 hover:bg-stone-700 text-white py-3 flex items-center justify-center transition-colors rounded-sm"
                            title="View Gallery"
                            onClick={(e) => openLightbox(product.galleryImages!, 0, e)}
                          >
                            <Eye size={16} />
                          </button>
                        )}
                      </div>
                  </div>

                  <div className="text-center mt-3 group-hover:-translate-y-1 transition-transform duration-300">
                    <h3 className="font-serif text-lg text-stone-900 mb-1 group-hover:text-gold-600 transition-colors">
                      {product.name}
                    </h3>

                    {showPromo && (
                      <div className="flex justify-center items-center gap-1 mb-2">
                        <span className="text-[9px] bg-red-600 text-white px-2 py-0.5 rounded-full uppercase font-bold flex items-center gap-1">
                          <Tag size={10} /> {promoLabel}
                        </span>
                        {promoPercentage && (
                          <span className="text-[9px] text-red-600 font-bold">
                            -{promoPercentage}%
                          </span>
                        )}
                      </div>
                    )}

                    {isPreOrder && !showPromo && (
                      <div className="flex justify-center mb-2">
                        <span className="text-[9px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <Clock size={10} /> Pre-order now
                        </span>
                      </div>
                    )}

                    {isPreLoved && !showPromo && (
                      <div className="flex justify-center mb-2">
                        <span className="text-[9px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <span>🔄</span> Pre-owned - Authentic
                        </span>
                      </div>
                    )}

                    {activeCategory === 'jewelry' &&
                      product.category === 'jewelry' &&
                      (product.weight || product.karat || product.goldType) && (
                        <div className="flex flex-wrap justify-center gap-1 mb-2 px-4">
                          {product.karat && (
                            <span className="text-[9px] uppercase tracking-wider border border-stone-200 px-1 rounded-sm text-stone-500">
                              {product.karat}
                            </span>
                          )}
                          {product.goldType && (
                            <span className="text-[9px] uppercase tracking-wider border border-stone-200 px-1 rounded-sm text-stone-500">
                              {product.goldType.replace('_', ' ')}
                            </span>
                          )}
                          {displayWeight && (
                            <span className="text-[9px] uppercase tracking-wider border border-stone-200 px-1 rounded-sm text-stone-500">
                              {displayWeight}g
                            </span>
                          )}
                        </div>
                      )}

                    <p className="text-xs text-stone-500 font-light mb-2 line-clamp-1 px-4">
                      {product.description}
                    </p>

                    <div className="flex flex-col items-center">
                      {showPromo && originalPrice && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-stone-400 line-through">
                            {formatPrice(originalPrice)}
                          </span>
                          {promoPercentage && (
                            <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">
                              -{promoPercentage}%
                            </span>
                          )}
                        </div>
                      )}

                      <span
                        className={`text-sm font-bold ${
                          showPromo ? 'text-red-600' : 'text-stone-900'
                        } tracking-wide`}
                      >
                        {formatPrice(displayPrice)}
                      </span>

                      {showPromo && formattedEndDate && (
                        <span className="text-[8px] text-stone-400 mt-1">
                          Ends {formattedEndDate}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[300] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-5 right-5 text-white hover:text-gold-600 z-10 bg-black/50 p-2 rounded-full transition-colors"
          >
            <X size={30} />
          </button>

          {lightboxImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) =>
                    prev > 0 ? prev - 1 : lightboxImages.length - 1
                  );
                }}
                className="absolute left-5 text-white hover:text-gold-600 bg-black/50 p-2 rounded-full transition-colors"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) =>
                    prev < lightboxImages.length - 1 ? prev + 1 : 0
                  );
                }}
                className="absolute right-5 text-white hover:text-gold-600 bg-black/50 p-2 rounded-full transition-colors"
              >
                <ChevronRight size={40} />
              </button>
            </>
          )}

          <img
            src={lightboxImages[currentImageIndex]}
            alt="Product view"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = PLACEHOLDER_IMAGE;
            }}
          />

          {lightboxImages.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 bg-black/70 p-2 rounded-lg overflow-x-auto max-w-[90vw]">
              {lightboxImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  className={`flex-shrink-0 w-12 h-12 rounded-sm overflow-hidden border-2 transition-colors ${
                    idx === currentImageIndex
                      ? 'border-gold-600'
                      : 'border-transparent hover:border-white/50'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumb ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = PLACEHOLDER_IMAGE;
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Shop;